import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';

import { createResource, useResource } from '@atlassian/react-resource-router';
import { JSM_REPACKAGING_ANNOUNCEMENT_USER_STATE_STORAGE_KEY } from './constants.tsx';
import type { JsmRepackagingAnnouncement } from './get-data/types.tsx';

const getDataLoader = () =>
	import(
		/*
			webpackChunkName: "async-resource-jsm-repackaging-announcement",
			jiraSpaEntry: "async-resource-jsm-repackaging-announcement"
		*/
		'./get-data'
	);

export const jsmRepackagingAnnouncementResource = createResource<
	JsmRepackagingAnnouncement | undefined
>({
	type: 'JSM_REPACKAGING_ANNOUNCEMENT',
	getKey: () => 'JSM_REPACKAGING_ANNOUNCEMENT',
	getDataLoader,
	maxAge: Infinity,
});

export const useJsmRepackagingAnnouncementResource = (accountId: AccountId | null) => {
	const { data, loading, error, refresh, update } = useResource(jsmRepackagingAnnouncementResource);
	const loadingRef = useRef(false);
	const errorRef = useRef<unknown>();
	const [userPropertiesError, setUserPropertiesError] = useState<Error>();
	errorRef.current = userPropertiesError || error;

	const dismissAnnouncement = useCallback(() => {
		const announcementDismissState = {
			shouldShow: false,
		};
		update(() => ({
			announcementDismissState,
		}));

		if (accountId) {
			if (loadingRef.current) {
				const err = new Error('User properties is loading');
				fireErrorAnalytics({
					meta: {
						id: 'useOgConsolidationUserPreference-update',
						packageName: 'jiraRouterResourcesJsmRepackagingAnnouncement',
						teamName: '50cent',
					},
					error: err,
					sendToPrivacyUnsafeSplunk: true,
				});
				setUserPropertiesError(err);
				return Promise.reject(err);
			}

			loadingRef.current = true;
			return setUserProperties(
				accountId,
				JSM_REPACKAGING_ANNOUNCEMENT_USER_STATE_STORAGE_KEY,
				JSON.stringify(announcementDismissState),
			)
				.catch((err) => {
					setUserPropertiesError(new Error('Error occured while setting user properties'));
					fireErrorAnalytics({
						meta: {
							id: 'useOgConsolidationUserPreference-update',
							packageName: 'jiraRouterResourcesJsmRepackagingAnnouncement',
							teamName: '50cent',
						},
						error: err,
						sendToPrivacyUnsafeSplunk: true,
					});
				})
				.finally(() => {
					loadingRef.current = false;
				});
		}
	}, [accountId, update]);

	useEffect(() => {
		if (!data && !loading && !error) {
			refresh();
		}
	}, [data, loading, refresh, error]);

	return useMemo(() => {
		if (errorRef.current || loading || !data || !accountId) {
			return {
				shouldShow: false,
				dismissAnnouncement,
			};
		}

		const { announcementDismissState } = data;

		if (!announcementDismissState) {
			return {
				shouldShow: false,
				dismissAnnouncement,
			};
		}

		return { ...announcementDismissState, dismissAnnouncement };
	}, [loading, data, accountId, dismissAnnouncement]);
};
